import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import "./css/ClientDocumentsUploadPage.css";

import { publicAuth as auth, publicStorage as storage } from "../firebase";
import { signInWithCustomToken, signOut } from "firebase/auth";
import { deleteObject, ref as storageRef, uploadBytesResumable } from "firebase/storage";

import logoMyKyc from "../images/mykyc-logo-blue-800px.png";
import logoMyKycPage from "../images/mykyc-logo-login-lightblue-1000.png";
import logoMyKycExpired from "../images/mykyc-logo-grey-1000.png";
import avatarIcon from "../images/avatar.svg";
import businessTradeIcon from "../images/businessTrade.svg";
import roiIcon from "../images/roi.svg";
import Lottie from "lottie-react";
import sofJson from "../images/SoF.json";
import sowJson from "../images/SoW.json";
import timerExpAnim from "../images/timer-exp-b.json";
import { MYKYC_API_BASE } from "../config/api";

const API_BASE = MYKYC_API_BASE;
const AUTH_ENDPOINT = "/myKyc/clients-side/doc-upload/auth";
const COMPLETE_ENDPOINT = "/myKyc/clients-side/doc-upload/complete";
const REQUIREMENTS_ENDPOINT = "/myKyc/clients-side/doc-upload/requirements";
const SAVE_DRAFT_ENDPOINT = "/myKyc/clients-side/doc-upload/save-draft";

/* -------------------- Helpers -------------------- */

function useIsMobileDocumentsUpload() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (event) => setIsMobile(event.matches);

    setIsMobile(mq.matches);

    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }

    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isMobile;
}

function safeStr(v) {
  return String(v || "").trim();
}

function sanitizeFilename(name) {
  const n = String(name || "").trim();
  if (!n) return "file";
  return n
    .replace(/[\/\\]/g, "_")
    .replace(/[^\w.\-() ]+/g, "_")
    .slice(0, 160);
}

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let val = n;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function getFriendlyFileType(file = {}) {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  const ext = name.includes(".") ? name.split(".").pop() : "";

  if (mime === "application/pdf" || ext === "pdf") return "PDF document";

  if (
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/msword" ||
    ext === "docx" ||
    ext === "doc"
  ) {
    return "Word document";
  }

  if (
    mime ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime === "application/vnd.ms-excel" ||
    ext === "xlsx" ||
    ext === "xls" ||
    ext === "csv"
  ) {
    return "Excel spreadsheet";
  }

  if (mime.startsWith("image/") || ["jpg", "jpeg", "png", "webp"].includes(ext)) {
    return "Image";
  }

  if (mime.startsWith("text/") || ext === "txt") return "Text document";

  return "File";
}

function makeId() {
  return (
    (globalThis.crypto?.randomUUID?.() ||
      `id_${Date.now()}_${Math.random().toString(16).slice(2)}`) + ""
  );
}

function restoreDraftItems(draftFiles, requirementList, requestId) {
  const validCardIds = new Set(
    (requirementList || []).map((r) => safeStr(r?.cardId)).filter(Boolean)
  );
  const currentRequestId = safeStr(requestId);

  return (Array.isArray(draftFiles) ? draftFiles : [])
    .filter((f) => {
      const fileRequestId = safeStr(f?.requestId);
      const belongsToCurrentRequest = currentRequestId
        ? fileRequestId === currentRequestId
        : true;
      return (
        belongsToCurrentRequest &&
        safeStr(f?.objectPath) &&
        validCardIds.has(safeStr(f?.cardId))
      );
    })
    .map((f) => ({
      id: makeId(),
      cardId: safeStr(f?.cardId),
      file: null,
      name: safeStr(f?.originalName) || safeStr(f?.objectPath).split("/").pop(),
      size: Number(f?.sizeBytes || 0),
      type: safeStr(f?.contentType) || "application/octet-stream",
      progress: 100,
      status: "done",
      error: "",
      downloadURL: safeStr(f?.downloadURL) || "",
      objectPath: safeStr(f?.objectPath),
      requestId: safeStr(f?.requestId) || currentRequestId,
      _restored: true,
    }));
}

function iso2ToFlagEmoji(iso2) {
  const cc = String(iso2 || "")
    .trim()
    .toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  const A = 0x1f1e6;
  return String.fromCodePoint(
    A + (cc.charCodeAt(0) - 65),
    A + (cc.charCodeAt(1) - 65)
  );
}

function stripMetaInTitle(title) {
  return String(title || "")
    .replace(/\s*\(Nationality:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s*\(Country:\s*[A-Za-z]{2}\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseRichLines(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return [];

  const out = [];
  const lines = raw
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean);

  for (const line of lines) {
    const noBullet = line.startsWith("•") ? line.slice(1).trim() : line;

    const lower = noBullet.toLowerCase();
    const isHeading =
      lower === "required:" ||
      lower.startsWith("provide at least one of:") ||
      /^provide at least \d+ of:/.test(lower) ||
      lower.startsWith("also provide (optional):");
    const isNote = /^\(.+\)$/.test(noBullet);

    if (isHeading) out.push({ type: "heading", text: noBullet });
    else if (isNote) out.push({ type: "note", text: noBullet });
    else out.push({ type: "bullet", text: noBullet });
  }

  return out;
}

function isConditionalEntityRequirement(req, lines) {
  if (req?.meta?.kind !== "entity") return false;
  const hasConditionNote = (lines || []).some(
    (line) => line.type === "note" && /depend|applicable|mainland|free zone/i.test(String(line.text || ""))
  );
  const bulletCount = (lines || []).filter((line) => line.type === "bullet").length;
  return hasConditionNote && bulletCount > 1;
}

function displayRequirementHeading(req, lines, headingText, headingIndex) {
  const text = String(headingText || "");
  if (text.toLowerCase() !== "required:") return text;

  if (isConditionalEntityRequirement(req, lines)) return "Provide at least 1 of:";

  let count = 0;
  for (let i = headingIndex + 1; i < lines.length; i++) {
    if (lines[i].type === "heading") break;
    if (lines[i].type === "bullet") count++;
  }
  return count === 1 ? "Provide the following:" : "Provide all of the following:";
}

function requirementLinesForDisplay(req, lines) {
  const indexedLines = (lines || []).map((line, index) => ({ ...line, _displayIndex: index }));
  if (!isConditionalEntityRequirement(req, indexedLines)) return indexedLines;
  return [
    ...indexedLines.filter((line) => line.type === "heading"),
    ...indexedLines.filter((line) => line.type === "bullet"),
    ...indexedLines.filter((line) => line.type === "note"),
  ];
}

function renderRequirementLineText(text) {
  const value = String(text || "");
  const match = value.match(/^(.*?)(\s*\([^()]+\))$/);
  if (!match) return value;
  return (
    <>
      {match[1].trimEnd()}
      <span className="docUploadReqTranslation">{match[2]}</span>
    </>
  );
}

function renderRequirementNoteText(req, lines, text) {
  const value = String(text || "").trim();
  if (!isConditionalEntityRequirement(req, lines)) return value;
  const unwrapped = value.replace(/^\((.*)\)$/s, "$1").trim();
  return `Note: ${unwrapped}`;
}

function lineLooksLikePassport(line) {
  const t = String(line || "").toLowerCase();
  return t.includes("passport");
}

function lineLooksLikePoa(line) {
  const t = String(line || "").toLowerCase();
  return (
    t.includes("proof of address") ||
    t.includes("proof of adress") ||
    t.includes("utility bill") ||
    t.includes("poa")
  );
}

function lineLooksLikeEntityRegister(line) {
  const t = String(line || "").toLowerCase();
  return (
    t.includes("kbis") ||
    t.includes("company register") ||
    t.includes("commercial register") ||
    t.includes("certificate of incorporation") ||
    t.includes("trade licence") ||
    t.includes("trade license")
  );
}

function getLineFlag({ req, line }) {
  const kind = req?.meta?.kind;

  if (kind === "individual") {
    if (lineLooksLikePassport(line)) {
      return iso2ToFlagEmoji(req?.meta?.nationalityIso2);
    }
    if (lineLooksLikePoa(line)) {
      return iso2ToFlagEmoji(req?.meta?.domicileIso2);
    }
  }

  return "";
}

function getHeadingFlag({ req, heading }) {
  if (req?.meta?.kind !== "entity") return "";
  const text = String(heading || "").trim().toLowerCase();
  if (
    text === "required:" ||
    text.startsWith("provide at least one of:") ||
    /^provide at least \d+ of:/.test(text) ||
    text === "provide the following:" ||
    text === "provide all of the following:"
  ) {
    return iso2ToFlagEmoji(req?.meta?.incorporationIso2);
  }
  return "";
}

function requiredFileCountForRequirement(req) {
  const lines = parseRichLines(req?.description);
  if (!lines.length) return 1;

  for (const line of lines) {
    if (line.type !== "heading") continue;
    const text = String(line.text || "").toLowerCase();
    if (text === "required:" && isConditionalEntityRequirement(req, lines)) return 1;
    if (text.includes("provide at least one of")) return 1;
    const atLeastMatch = text.match(/provide at least\s+(\d+)\s+of/);
    if (atLeastMatch) return Math.max(1, Number(atLeastMatch[1]) || 1);
  }

  const firstOptionalIndex = lines.findIndex(
    (line) =>
      line.type === "heading" &&
      String(line.text || "").toLowerCase().includes("optional")
  );
  const requiredLines =
    firstOptionalIndex >= 0 ? lines.slice(0, firstOptionalIndex) : lines;
  const countable = requiredLines.filter((line) => line.type === "bullet").length;
  return Math.max(1, countable || 0);
}

function getPartyHeaderFlag(req) {
  return "";
}

function getPartyIcon(kind) {
  if (kind === "entity") return businessTradeIcon;
  if (kind === "individual") return avatarIcon;
  return roiIcon;
}

function getPartyPill(kind) {
  if (kind === "entity") return "Entity";
  if (kind === "individual") return "Individual";
  return "";
}

/* -------------------- Component -------------------- */

export function ClientDocumentsUploadExperience({ isMobile = false } = {}) {
  const { clientId: clientIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();

  const clientId = safeStr(clientIdParam);
  const token = safeStr(searchParams.get("token"));

  const fileInputRef = useRef(null);
  const autoUploadRef = useRef(false);
  const [activeCardId, setActiveCardId] = useState(""); // ✅ now stable cardId

  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [session, setSession] = useState(null);

  const [items, setItems] = useState([]); // [{ id, cardId, file, ... }]
  const [busy, setBusy] = useState(false);

  const [dragOverCardId, setDragOverCardId] = useState("");

  const queuedCount = useMemo(
    () => items.filter((x) => x.status === "queued").length,
    [items]
  );
  const doneCount = useMemo(
    () => items.filter((x) => x.status === "done").length,
    [items]
  );

  const [requirements, setRequirements] = useState([]);

  // -- Modal end confirmation ---
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showRequirementsUpdatedModal, setShowRequirementsUpdatedModal] =
    useState(false);
  // const [showDonePage, setShowDonePage] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false); // separate from upload busy
  const doneItems = useMemo(
    () => items.filter((x) => x.status === "done" && x.objectPath),
    [items]
  );

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();

    async function start() {
      setLoading(true);
      setApiError("");

      try {
        try {
          await signOut(auth);
        } catch {}

        if (!clientId || !token) {
          throw new Error("Invalid link. Missing clientId or token.");
        }

        // 1) AUTH
        const resp = await fetch(`${API_BASE}${AUTH_ENDPOINT}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientId, token }),
          signal: ac.signal,
        });

        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data?.ok !== true) {
          throw new Error(
            data?.error ||
              "Upload link error. Please contact your compliance team and request a new link."
          );
        }

        const firebaseToken = safeStr(data?.firebaseToken);
        if (!firebaseToken)
          throw new Error("Server did not return firebaseToken.");

        // 2) Firebase sign-in
        const cred = await signInWithCustomToken(auth, firebaseToken);
        await cred.user.getIdToken(true);

        if (!alive) return;

        const nextSession = {
          sessionId: safeStr(data?.sessionId),
          uploadPrefix: safeStr(data?.uploadPrefix),
          requestId: safeStr(data?.requestId),
          maxFileBytes: Number(data?.maxFileBytes || 25 * 1024 * 1024),
        };
        setSession(nextSession);

        // 3) Requirements
        try {
          const rResp = await fetch(`${API_BASE}${REQUIREMENTS_ENDPOINT}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ clientId, token }),
            signal: ac.signal,
          });

          const rData = await rResp.json().catch(() => ({}));
          if (rResp.ok && rData?.ok === true) {
            const list = Array.isArray(rData.requirements)
              ? rData.requirements
              : [];
            if (!alive) return;

            setRequirements(list);

            // ✅ set first active card by stable cardId
            if (list.length) {
              const firstCid = safeStr(list[0]?.cardId);
              if (firstCid) setActiveCardId(firstCid);
            }

            // ✅ Restore draft files only for this upload request.
            // A client may receive a new magic link after staff deletes a previous
            // request; old draft files must not make the new request look complete.
            const draft = Array.isArray(rData.draftFiles) ? rData.draftFiles : [];
            const restored = restoreDraftItems(
              draft,
              list,
              safeStr(rData.requestId) || nextSession.requestId
            );
            setItems(restored);
          } else {
            console.warn("[requirements] non-ok response", rData);
          }
        } catch (e) {
          if (e?.name === "AbortError") return;
          console.error("[requirements] failed:", e);
          if (!alive) return;
          setApiError(
            `Requirements load failed: ${e?.message || "unknown error"}`
          );
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!alive) return;
        setApiError(e?.message || "Upload link error.");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    start();

    return () => {
      alive = false;
      ac.abort();
    };
  }, [clientId, token]);

  function openFilePicker(forCardId) {
    if (!session) return;
    setActiveCardId(String(forCardId || ""));
    fileInputRef.current?.click?.();
  }

  useEffect(() => {
    if (!session) return;
    if (autoUploadRef.current) return;
    const hasQueued = items.some((x) => x.status === "queued");
    if (!hasQueued) return;
    autoUploadRef.current = true;
    uploadAll().finally(() => {
      autoUploadRef.current = false;
      setItems((prev) => [...prev]);
    });
  }, [items, busy, session]); // eslint-disable-line

  function addFiles(fileList, cardId) {
    if (!session) return;
    const maxBytes = Number(session.maxFileBytes || 25 * 1024 * 1024);

    const cid = String(cardId || "").trim();
    if (!cid) return;

    const arr = Array.from(fileList || []);
    const next = [];

    for (const f of arr) {
      if (!f) continue;

      const size = Number(f.size || 0);
      if (!Number.isFinite(size) || size <= 0) continue;

      const name = sanitizeFilename(f.name);
      const type = f.type || "application/octet-stream";

      if (size > maxBytes) {
        next.push({
          id: makeId(),
          cardId: cid,
          file: f,
          name,
          size,
          type,
          progress: 0,
          status: "error",
          error: `File too large (max ${formatBytes(maxBytes)})`,
          downloadURL: "",
          objectPath: "",
        });
        continue;
      }

      next.push({
        id: makeId(),
        cardId: cid,
        file: f,
        name,
        size,
        type,
        progress: 0,
        status: "queued",
        error: "",
        downloadURL: "",
        objectPath: "",
      });
    }

    setItems((prev) => {
      const seen = new Set(
        prev.map((x) => `${x.cardId}__${x.name}__${x.size}`)
      );
      const merged = [...prev];
      for (const n of next) {
        const k = `${n.cardId}__${n.name}__${n.size}`;
        if (seen.has(k)) continue;
        merged.push(n);
        seen.add(k);
      }
      return merged;
    });
  }

  function buildDraftFiles(nextItems) {
    return (nextItems || [])
      .filter((x) => x.status === "done" && x.objectPath)
      .map((x) => {
        const cid = String(x.cardId || "").trim();
        const req = (requirements || []).find(
          (r) => String(r?.cardId || "").trim() === cid
        );
        return {
          objectPath: x.objectPath,
          originalName: x.name,
          contentType: x.type,
          sizeBytes: x.size,
          downloadURL: x.downloadURL || null,
          cardId: cid || null,
          cardTitle: String(req?.title || "").trim() || null,
          requestId: session?.requestId || null,
        };
      });
  }

  async function saveDraftFiles(nextItems) {
    if (!session) return;
    await fetch(`${API_BASE}${SAVE_DRAFT_ENDPOINT}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        token,
        requestId: session.requestId || null,
        files: buildDraftFiles(nextItems),
      }),
    });
  }

  function removeItem(id) {
    if (busy) return;
    const target = items.find((x) => x.id === id);
    const nextItems = items.filter((x) => x.id !== id);
    setItems(nextItems);

    if (target?.objectPath) {
      deleteObject(storageRef(storage, target.objectPath)).catch(() => {});
    }
    saveDraftFiles(nextItems).catch(() => {});
  }

  function onDropCard(e, cardId) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId("");

    const files = e.dataTransfer?.files;
    if (files && files.length) addFiles(files, cardId);
  }
  function onDragOverCard(e, cardId) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId(String(cardId));
  }
  function onDragLeaveCard(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCardId("");
  }

  function shouldPromptAfterUpload(nextItems, uploadedCardIds = null) {
    const justQueuedCards =
      uploadedCardIds instanceof Set
        ? uploadedCardIds
        : new Set(
            (items || [])
              .filter((x) => x.status === "queued")
              .map((x) => String(x.cardId || "").trim())
              .filter(Boolean)
          );

    if (justQueuedCards.size === 0) return false;

    const requiredCards = (requirements || []).filter((req) =>
      String(req?.cardId || "").trim()
    );
    if (!requiredCards.length) return false;

    const readiness = requiredCards.map((req) => {
      const cardId = String(req?.cardId || "").trim();
      const requiredCount = requiredFileCountForRequirement(req);
      const doneForCard = (nextItems || []).filter(
        (x) =>
          String(x.cardId || "").trim() === cardId &&
          x.status === "done" &&
          x.objectPath
      ).length;
      return {
        cardId,
        title: safeStr(req?.title),
        requiredCount,
        doneForCard,
        complete: doneForCard >= requiredCount,
      };
    });
    const allComplete = readiness.every((row) => row.complete);
    console.log("[documents-upload] submit prompt readiness", {
      allComplete,
      justQueuedCards: Array.from(justQueuedCards),
      readiness,
    });
    return allComplete;
  }

  function getCurrentItems() {
    return new Promise((resolve) =>
      setItems((prev) => {
        resolve(prev);
        return prev;
      })
    );
  }

  async function uploadAll() {
    if (!session) return;

    if (!auth.currentUser) {
      setApiError("Upload session not authenticated. Please refresh the link.");
      return;
    }

    setApiError("");

    try {
      const u = auth.currentUser;
      const tok = await u.getIdTokenResult(true);

      const claimPrefix = String(tok?.claims?.uploadPrefix || "");
      const claimSessionId = String(tok?.claims?.sessionId || "");

      if (!claimPrefix) {
        throw new Error(
          "Missing uploadPrefix in token claims. Refresh the link."
        );
      }

      const uploadedCardIds = new Set();

      // Upload sequentially, but keep checking for files added while this loop runs.
      // This lets users continue dropping documents without opening parallel tasks.
      while (true) {
        const currentItems = await getCurrentItems();
        const queuedItems = currentItems.filter((x) => x.status === "queued");
        if (!queuedItems.length) break;

        for (const item of queuedItems) {
          const stillQueued = (await getCurrentItems()).some(
            (x) => x.id === item.id && x.status === "queued"
          );
          if (!stillQueued) continue;

          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            // ✅ folder is stable cardId (already safe from backend)
            const objectPath = `${claimPrefix}/${String(
              item.cardId || "card"
            )}/${Date.now()}_${sanitizeFilename(item.name)}`;

            const r = storageRef(storage, objectPath);

            setItems((prev) =>
              prev.map((x) =>
                x.id === item.id
                  ? { ...x, status: "uploading", progress: 1, objectPath }
                  : x
              )
            );

            const task = uploadBytesResumable(r, item.file, {
              contentType: item.type,
            });

            task.on(
              "state_changed",
              (snap) => {
                const pct = snap.totalBytes
                  ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                  : 0;

                setItems((prev) =>
                  prev.map((x) =>
                    x.id === item.id ? { ...x, progress: pct } : x
                  )
                );
              },
              (err) => {
                setItems((prev) =>
                  prev.map((x) =>
                    x.id === item.id
                      ? {
                          ...x,
                          status: "error",
                          error: err?.message || "Upload failed",
                          progress: 0,
                        }
                      : x
                  )
                );
                resolve();
              },
              () => {
                uploadedCardIds.add(String(item.cardId || "").trim());
                setItems((prev) =>
                  prev.map((x) =>
                    x.id === item.id
                      ? { ...x, status: "done", progress: 100 }
                      : x
                  )
                );
                resolve();
              }
            );
          });
        }
      }

      // ✅ Persist draft so client can see uploaded files if they reload before submitting
      try {
        const currentItems = await getCurrentItems();
        saveDraftFiles(currentItems).catch(() => {});
      } catch (_) {}

      const latestItems = await getCurrentItems();
      if (shouldPromptAfterUpload(latestItems, uploadedCardIds)) {
        setShowFinishModal(true);
      }
    } catch (e) {
      setApiError(e?.message || "Upload failed.");
    }
  }

  async function finishAndSubmit() {
    if (!session) return;

    if (!auth.currentUser) {
      setApiError("Upload session not authenticated. Please refresh the link.");
      return;
    }

    setSubmitBusy(true);
    setApiError("");

    try {
      const u = auth.currentUser;
      const tok = await u.getIdTokenResult(true);

      const claimSessionId = String(tok?.claims?.sessionId || "");
      const sid = claimSessionId || session.sessionId;

      // Build uploaded list from ALL done items in this session (could be multiple rounds)
      const uploaded = (items || [])
        .filter((x) => x.status === "done" && x.objectPath)
        .map((x) => {
          const cid = String(x.cardId || "").trim();
          const req = (requirements || []).find(
            (r) => String(r?.cardId || "").trim() === cid
          );
          const cardTitle = String(req?.title || "").trim();

          return {
            objectPath: x.objectPath,
            originalName: x.name,
            contentType: x.type,
            sizeBytes: x.size,
            downloadURL: x.downloadURL || null,
            cardId: cid || null,
            cardTitle: cardTitle || null,
            requestId: session.requestId || null,
          };
        });

      if (!uploaded.length) {
        throw new Error("No uploaded files to submit.");
      }

      const resp = await fetch(`${API_BASE}${COMPLETE_ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId,
          token,
          sessionId: sid,
          requestId: session.requestId || null,
          files: uploaded,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok !== true) {
        if (data?.code === "DOCUMENT_REQUIREMENTS_CHANGED") {
          const list = Array.isArray(data.requirements)
            ? data.requirements
            : [];
          const requestId = safeStr(data.requestId) || session.requestId;
          setRequirements(list);
          setItems(restoreDraftItems(data.draftFiles, list, requestId));

          const firstCurrentCardId = safeStr(list[0]?.cardId);
          if (firstCurrentCardId) setActiveCardId(firstCurrentCardId);

          setShowFinishModal(false);
          setShowRequirementsUpdatedModal(true);
          return;
        }

        throw new Error(
          data?.error ||
            "Upload finished, but could not finalize on server. Please contact support."
        );
      }

      // ✅ Now we are truly finished: close modal, sign out, show done page
      setShowFinishModal(false);
      try {
        await signOut(auth);
      } catch {}
      // redirect to thanks page (replace avoids “back” returning to upload)
      nav(`/c/documents-upload/${encodeURIComponent(clientId)}/thanks`, {
        replace: true,
        state: {
          submittedCount: uploaded.length,
          clientName: clientDisplayName,
          submittedAt: new Date().toISOString(),
        },
      });
      // setShowDonePage(true);
    } catch (e) {
      setApiError(e?.message || "Final submit failed.");
    } finally {
      setSubmitBusy(false);
    }
  }

  const itemsForCard = (cardId) =>
    items.filter((x) => String(x.cardId) === String(cardId));

  // Group requirements into "party cards" and "SoW"
  const grouped = useMemo(() => {
    const map = new Map();

    for (const r of requirements || []) {
      const kind = safeStr(r?.meta?.kind);
      const partyName = safeStr(r?.meta?.partyName);
      const partyRole = safeStr(r?.meta?.partyRole);

      // Group by canonical partyId so distinct parties with the same name stay separate.
      const groupKey =
        kind === "individual" || kind === "entity"
          ? `party::${kind}::${
              safeStr(r?.meta?.partyId) || partyName || safeStr(r?.cardId)
            }`
          : `other::${kind || "misc"}::${
              safeStr(r?.meta?.sowKey) || safeStr(r?.cardId)
            }`;

      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          kind,
          partyName,
          partyRole,
          requirements: [],
        });
      }
      map.get(groupKey).requirements.push(r);
    }

    return Array.from(map.values());
  }, [requirements]);

  const clientDisplayName = useMemo(() => {
    const firstParty = (grouped || []).find(
      (g) => g.kind === "individual" || g.kind === "entity"
    );
    return firstParty?.partyName || "Documents Upload";
  }, [grouped]);

  const identificationGroups = useMemo(
    () => grouped.filter((g) => g.kind === "individual" || g.kind === "entity"),
    [grouped]
  );

  const supportingDocumentGroups = useMemo(
    () => grouped.filter((g) => g.kind !== "individual" && g.kind !== "entity"),
    [grouped]
  );

  /* ------- Main progress bar bottom ------- */

  const totals = useMemo(() => {
    const list = Array.isArray(items) ? items : [];

    const totalBytes = list.reduce((s, it) => s + (Number(it.size) || 0), 0);

    const doneBytes = list
      .filter((it) => it.status === "done")
      .reduce((s, it) => s + (Number(it.size) || 0), 0);

    const uploadingBytes = list
      .filter((it) => it.status === "uploading")
      .reduce((s, it) => {
        const size = Number(it.size) || 0;
        const pct = Math.max(0, Math.min(100, Number(it.progress) || 0));
        return s + (size * pct) / 100;
      }, 0);

    const totalTransferred = doneBytes + uploadingBytes;

    const pct =
      totalBytes > 0 ? Math.round((totalTransferred / totalBytes) * 100) : 0;

    return {
      totalBytes,
      totalTransferred,
      pct,
    };
  }, [items]);

  const isUploadingAny = useMemo(
    () => items.some((x) => x.status === "uploading"),
    [items]
  );

  const canSubmitDocuments =
    doneItems.length > 0 && queuedCount === 0 && !isUploadingAny && !busy;

  const renderPartyList = (groups) => (
    <div className="docUploadPartyList">
      {groups.map((g) => {
        const isParty = g.kind === "individual" || g.kind === "entity";
        const pill = getPartyPill(g.kind);
        const icon = getPartyIcon(g.kind);

        const firstReq = g.requirements?.[0] || null;
        const headerFlag = isParty ? getPartyHeaderFlag(firstReq) : "";

        const headerName = isParty
          ? g.partyName || "Client"
          : isMobile
          ? (g.requirements?.[0]?.title
              ? stripMetaInTitle(g.requirements[0].title)
              : "Requirements")
          : "Source of Wealth • " +
            (g.requirements?.[0]?.title
              ? stripMetaInTitle(g.requirements[0].title)
              : "Requirements");

        const subLine = isParty
          ? g.partyRole || "Customer"
          : "Supporting documents required";

        return (
          <div
            key={g.key}
            className={[
              "docUploadPartyCard",
              isParty ? "isParty" : "isSupportingDocs",
              g.kind === "individual" ? "isIndividual" : "",
              g.kind === "entity" ? "isEntity" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="docUploadPartyHeader">
                <div className="docUploadPartyHeaderLeft">
                  <div className="docUploadPartyAvatar">
                  <img src={icon} alt="" aria-hidden="true" />
                </div>

                <div className="docUploadPartyHeaderText">
                  <div className="docUploadPartyNameRow">
                    <div className="docUploadPartyName">
                      {headerFlag ? (
                        <span className="docUploadFlag" title="Country">
                          {headerFlag}
                        </span>
                      ) : null}
                      {headerName}
                    </div>

                    {isParty && pill ? (
                      <span
                        className={[
                          "docUploadPill",
                          g.kind === "individual"
                            ? "isIndividual"
                            : "isEntity",
                        ].join(" ")}
                      >
                        {pill}
                      </span>
                    ) : null}
                  </div>

                  <div className="docUploadPartySub">{subLine}</div>
                </div>
              </div>
            </div>

            <div className="docUploadPartyBody">
              {g.requirements.map((r) => {
                const cid = String(r?.cardId || "");
                const cardItems = itemsForCard(cid);
                const lines = parseRichLines(r?.description);

                return (
                  <div key={cid} className="docUploadThreeCardRow">
                    <div className="docUploadTwoCardsTop">
                      <div className="docUploadReqCard">
                        {lines.length ? (
                          <div className="docUploadReqBullets">
                            {(() => {
                              const kind = r?.meta?.kind;
                              const hasHeading = lines.some(
                                (x) => x.type === "heading"
                              );

                              return (
                                <>
                                  {kind === "individual" &&
                                  !hasHeading ? (
                                    <div className="docUploadReqHeading">
                                      {lines.filter(l => l.type === "bullet").length === 1
                                        ? "Provide the following:"
                                        : "Provide all of the following:"}
                                    </div>
                                  ) : null}

                                  {requirementLinesForDisplay(r, lines).map((ln, idx) => {
                                    if (ln.type === "heading") {
                                      const displayText = displayRequirementHeading(
                                        r,
                                        lines,
                                        ln.text,
                                        ln._displayIndex ?? idx
                                      );
                                      const headingFlag = getHeadingFlag({
                                        req: r,
                                        heading: displayText,
                                      });
                                      return (
                                        <div
                                          key={`${cid}_h_${idx}`}
                                          className={`docUploadReqHeading${
                                            headingFlag ? " docUploadReqHeading--withFlag" : ""
                                          }`}
                                        >
                                          <span>{displayText}</span>
                                          {headingFlag ? (
                                            <span
                                              className="docUploadFlag docUploadFlag--heading"
                                              title="Country"
                                            >
                                              {headingFlag}
                                            </span>
                                          ) : null}
                                        </div>
                                      );
                                    }

                                    const flag = getLineFlag({
                                      req: r,
                                      line: ln.text,
                                    });

                                    return (
                                      <div
                                        key={`${cid}_b_${idx}`}
                                        className={
                                          ln.type === "note"
                                            ? `docUploadReqNote${
                                                isConditionalEntityRequirement(r, lines)
                                                  ? " docUploadReqNote--leftAligned"
                                                  : ""
                                              }`
                                            : "docUploadReqBullet"
                                        }
                                      >
                                        {ln.type === "note" ? null : (
                                          <span className="docUploadReqBulletDot">
                                            •
                                          </span>
                                        )}
                                        <span className="docUploadReqBulletText">
                                          {ln.type === "note"
                                            ? renderRequirementNoteText(r, lines, ln.text)
                                            : renderRequirementLineText(ln.text)}
                                        </span>
                                        {flag ? (
                                          <span
                                            className="docUploadFlag"
                                            title="Country"
                                          >
                                            {flag}
                                          </span>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </>
                              );
                            })()}
                          </div>
                        ) : r.description ? (
                          <div
                            className="docUploadReqDesc"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {r.description}
                          </div>
                        ) : null}
                      </div>

                      <div className="docUploadUploadCard">
                        <div
                          className={[
                            "docUploadDropZone docUploadDropZoneStack",
                            dragOverCardId === cid ? "isDragOver" : "",
                          ].join(" ")}
                          onDrop={(e) => onDropCard(e, cid)}
                          onDragOver={(e) => onDragOverCard(e, cid)}
                          onDragLeave={onDragLeaveCard}
                          onClick={() => openFilePicker(cid)}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="docUploadDropTopRow">
                            <div className="docUploadDropIcon">
                              <i className="bi bi-cloud-arrow-up" />
                            </div>

                            <div className="docUploadDropText">
                              <div className="docUploadDropTitle">
                                Drop files here
                              </div>
                              <div className="docUploadDropSub">
                                or click to browse
                              </div>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn docUploadBtnOutline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openFilePicker(cid);
                            }}
                            disabled={!session}
                          >
                            {isMobile ? "Choose file" : "Browse"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {cardItems.length > 0 ? (
                      <div className="docUploadFilesCard">
                        <div className="docUploadFilesTitle">
                          Uploaded documents
                        </div>

                        <div className="docUploadListWrap">
                          {cardItems.map((it, idx) => (
                            <div
                              key={it.id}
                              className={[
                                "docUploadRow",
                                idx === 0 ? "isFirst" : "",
                                it.status === "error" ? "isError" : "",
                              ].join(" ")}
                            >
                              <div className="docUploadRowIcon">
                                {it.status === "done" ? (
                                  <i className="bi bi-check-circle-fill" />
                                ) : it.status === "error" ? (
                                  <i className="bi bi-exclamation-triangle-fill" />
                                ) : it.status === "uploading" ? (
                                  <i className="bi bi-arrow-repeat" />
                                ) : (
                                  <i className="bi bi-file-earmark" />
                                )}
                              </div>

                              <div className="docUploadRowMain">
                                <div className="docUploadRowName">
                                  {it.name}
                                </div>
                                <div className="docUploadRowMeta">
                                  {formatBytes(it.size)} •{" "}
                                  {getFriendlyFileType(it)}
                                  {it.status === "error" && it.error ? (
                                    <span className="docUploadRowErr">
                                      {" "}
                                      • {it.error}
                                    </span>
                                  ) : null}
                                </div>

                                {(it.status === "uploading" ||
                                  it.status === "done") && (
                                  <div className="docUploadProgress">
                                    <div
                                      className="docUploadProgressBar"
                                      style={{
                                        width: `${Math.max(
                                          0,
                                          Math.min(
                                            100,
                                            it.progress || 0
                                          )
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                )}
                              </div>

                              <div className="docUploadRowRight">
                                <div className="docUploadPct">
                                  {it.status === "done"
                                    ? "100%"
                                    : it.status === "uploading"
                                    ? `${it.progress || 0}%`
                                    : ""}
                                </div>

                                <button
                                  type="button"
                                  className="btn docUploadBtnDelete"
                                  onClick={() => removeItem(it.id)}
                                  disabled={
                                    busy || it.status === "uploading"
                                  }
                                  title="Remove"
                                >
                                  <i className="bi bi-trash3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const pageClassName = isMobile
    ? "docUploadPage docUploadMobilePage"
    : "docUploadPage";

  const renderExpiredLink = () => (
    <div className={`${pageClassName} docUploadExpiredPage`}>
      <div className="docUploadExpiredShell">
        <div className="docUploadExpiredContent">
        <motion.div
          className="docUploadExpiredHeader"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="docUploadExpiredBadge">
            Secure Upload · Link expired
          </div>

          <Lottie
            animationData={timerExpAnim}
            loop={true}
            lottieRef={(ref) => ref?.setSpeed(0.5)}
            className="docUploadExpiredLottie"
            aria-hidden="true"
          />

          <motion.h1
            className="docUploadExpiredTitle"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.38,
              delay: 0.14,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            This link can’t be used
          </motion.h1>

          <motion.p
            className="docUploadExpiredLead"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.38,
              delay: 0.22,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            Your secure upload link has expired or is no longer valid.
          </motion.p>
        </motion.div>

        <motion.div
          className="docUploadExpiredCard"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, delay: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="docUploadExpiredCardTitle">What to do next</div>
          <div className="docUploadExpiredCardItem">
            <span className="docUploadExpiredCardDot" aria-hidden="true" />
            <span>Please request a new secure link from the staff team.</span>
          </div>
          <div className="docUploadExpiredCardItem">
            <span className="docUploadExpiredCardDot" aria-hidden="true" />
            <span>If you believe this is an error, you can try refreshing the page.</span>
          </div>

          <div className="docUploadExpiredActions">
            <button
              type="button"
              className="btn docUploadExpiredRetryBtn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </motion.div>
        </div>

        <motion.div
          className="docUploadFooterBrand"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
        >
          <img src={logoMyKycExpired} alt="myKYC" className="docUploadFooterLogo" />
        </motion.div>
      </div>
    </div>
  );

  /* -------------------- RENDER -------------------- */

  if (apiError && !session) {
    return renderExpiredLink();
  }

  return (
    <>
      <AnimatePresence>
        {loading && (
          <motion.div
            key="docUploadOverlay"
            className="docUploadLoadingOverlay"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            <div className="docUploadLoadingInner">
              <img src={logoMyKyc} alt="myKYC" className="docUploadLoadingLogo" />
              <div className="appDocsUploadLoader" aria-hidden="true" />
              <div className="appDocsUploadTextLoader" aria-label="Loading" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: loading ? 0 : 1 }}
        transition={{ duration: 0.35, ease: "easeInOut" }}
      >
    <div className={pageClassName}>
      <div className="docUploadHero">
        <div className={isMobile ? "docUploadHeroInnerMobile" : "container-fluid docUploadHeroInner"}>
          <div className="docUploadBrand">
            <img className="docUploadLogo" src={logoMyKycPage} alt="MyKYC" />
            <div className="docUploadBrandText">
              <div className="docUploadBrandTitle">Secure Document Upload</div>
              <div className="docUploadBrandSub">
                Please upload the documents requested below. Your files are sent
                securely to your compliance team for review.
              </div>
              {isMobile ? (
                <div className="docUploadMobileClientCard">
                  {clientDisplayName}
                </div>
              ) : null}
            </div>
          </div>

          <div className="docUploadHeroInfo">
            <div className={isMobile ? "docUploadInfoItemMobile" : "docUploadInfoItem"}>
              <div className="docUploadInfoLabel">How it works</div>
              <div className="docUploadInfoText">
                {/* • */}
                1. Add files under each requirement{isMobile ? "." : " (drag & drop or browse)."}
                <br />
                2. When ready, click <b>Submit documents</b> at the bottom.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid docUploadContainer">
        {apiError ? (
          <div className="alert alert-warning">{apiError}</div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            addFiles(e.target.files, activeCardId);
            e.target.value = "";
          }}
        />

        {isMobile ? (
          <>
            <div className="docUploadMainCard docUploadMobileSectionCard">
              <div className="docUploadMobileSectionHead">
                <div className="docUploadMobileSectionLottie">
                  <Lottie animationData={sofJson} loop />
                </div>
                <div className="docUploadMobileSectionInfo">
                  <div className="docUploadSectionTitle">Identification Documents</div>
                  <div className="docUploadSectionSubtitle">
                    Passports, proofs of address, corporate documents
                  </div>
                </div>
              </div>
              <div className="docUploadMainBody">
                {renderPartyList(identificationGroups)}
              </div>
            </div>

            {supportingDocumentGroups.length > 0 ? (
              <div className="docUploadMainCard docUploadMobileSectionCard docUploadMobileSectionCard--sow">
                <div className="docUploadMobileSectionHead">
                  <div className="docUploadMobileSectionLottie">
                    <Lottie animationData={sowJson} loop />
                  </div>
                  <div className="docUploadMobileSectionInfo">
                    <div className="docUploadSectionTitle">Source of Wealth</div>
                    <div className="docUploadSectionSubtitle">
                      Corroboration of the origin of your wealth
                    </div>
                  </div>
                </div>
                <div className="docUploadMainBody">
                  {renderPartyList(supportingDocumentGroups)}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="docUploadMainCard">
            <div className="docUploadSectionHead">
              <div className="docUploadSectionTitle">{clientDisplayName}</div>
              <div className="docUploadSectionSubtitle">
                Documents upload checklist • Then click <b>Submit documents</b> at the
                bottom.
              </div>
            </div>
            <div className="docUploadMainBody">
              {renderPartyList(grouped)}
            </div>
          </div>
        )}
      </div>

      <div className="docUploadActionsBar">
        <div className="docUploadActionsInner docUploadActionsRow">
          <div className="docUploadStats">
            Uploaded: {doneCount} {doneCount === 1 ? "document" : "documents"}
          </div>

          {isUploadingAny ? (
            <div className="docUploadOverallInline">
              <div className="docUploadProgress docUploadProgressOverall">
                <div
                  className="docUploadProgressBar"
                  style={{ width: `${totals.pct}%` }}
                />
              </div>
              {/* <div className="docUploadOverallInlineMeta">{totals.pct}%</div> */}
            </div>
          ) : (
            <div className="docUploadOverallInline" />
          )}

          <button
            type="button"
            className="btn docUploadBtnPrimaryX"
            onClick={finishAndSubmit}
            disabled={!canSubmitDocuments || submitBusy}
          >
            {submitBusy ? "Submitting..." : "Submit documents"}
          </button>
        </div>
      </div>

      {showFinishModal && (
        <div className="docUploadModalOverlay">
          <div className="docUploadModalCard">
            <div className="docUploadModalTitle">Upload complete</div>
            <div className="docUploadModalText">
              Do you want to submit these documents now, or add more files?
            </div>

            <div className="docUploadModalActions">
              <button
                type="button"
                className="btn docUploadBtnOutline"
                onClick={() => setShowFinishModal(false)}
                disabled={submitBusy}
              >
                Add more files
              </button>

              <button
                type="button"
                className="btn docUploadBtnPrimaryX"
                onClick={finishAndSubmit}
                disabled={submitBusy}
              >
                {submitBusy ? "Submitting…" : "I’m finished"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRequirementsUpdatedModal && (
        <div className="docUploadModalOverlay">
          <div className="docUploadModalCard">
            <div className="docUploadModalTitle">
              Document requirements were updated
            </div>
            <div className="docUploadModalText">
              The requested document list has changed while you were uploading.
              We kept any files that still match the current requirements.
              Please review the updated list and upload any newly required
              documents.
            </div>

            <div className="docUploadModalActions">
              <button
                type="button"
                className="btn docUploadBtnPrimaryX"
                onClick={() => setShowRequirementsUpdatedModal(false)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* <div className="docUploadActionsBar">
        <div className="docUploadActionsInner">
          <div className="docUploadStats">
            Uploaded: {doneCount} {doneCount === 1 ? "document" : "documents"}
          </div>
      
          <button
            type="button"
            className="btn docUploadBtnPrimaryX"
            onClick={uploadAll}
            disabled={busy || !session || queuedCount === 0}
          >
            {busy
              ? "Uploading…"
              : `Upload${queuedCount ? ` (${queuedCount})` : ""}`}
          </button>
        </div>
      </div> */}
    </div>
      </motion.div>
    </>
  );
}

export default function ClientDocumentsUploadPage() {
  const { clientId: clientIdParam } = useParams();
  const location = useLocation();
  const isMobile = useIsMobileDocumentsUpload();
  const clientId = safeStr(clientIdParam);

  if (isMobile) {
    return (
      <Navigate
        to={`/c/documents-upload-mobile/${encodeURIComponent(clientId)}${
          location.search || ""
        }`}
        replace
      />
    );
  }

  return <ClientDocumentsUploadExperience />;
}
